import type { App } from 'vue';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import VueAxios from 'vue-axios';
import JwtService from '@/core/services/JwtService';
import SwalManager from '../helpers/SwalManager';
import router from '@/router';
import type BaseResponse from '@/core/models/Base/BaseResponse';
import type InvokeTokenResponse from '@/core/models/RefreshToken/IInvokeTokenResponse';
import type GenerateRefreshTokenResponse from '@/core/models/RefreshToken/IGenerateRefreshTokenResponse';
import { useLoaderStore } from '@/stores/loader';
import { useAuthStore } from '@/stores/auth';
import BaseHandlerService from './BaseHandlerService';

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
	/**
	 * @description property to share vue instance
	 */
	public static vueInstance: App;

	/**
	 * @description initialize vue axios
	 */
	public init(app: App<Element>) {
		const loaderStore = useLoaderStore();

		ApiService.vueInstance = app;
		ApiService.vueInstance.use(VueAxios, axios);
		ApiService.vueInstance.axios.defaults.baseURL =
			import.meta.env.VITE_APP_API_URL;

		axios.interceptors.request.use(
			(config): any => {
				if (config.headers.SHOW_LOADER) loaderStore.isLoading = true;

				return config;
			},
			(error) => {
				return Promise.reject(error);
			}
		);

		axios.interceptors.response.use(
			(response) => {
				loaderStore.isLoading = false;
				return response.data;
			},
			async (error) => {
				loaderStore.isLoading = false;

				if (error.response) {
					switch (error.response.status) {
						case 400:
						case 500:
							SwalManager.error(error.response.data.errors);
							break;

						// ==== unauthorized
						case 401:
							return await this.invokeRefreshToken(error);

						// ==== forbidden
						case 403:
							SwalManager.error(
								'The action you are trying to access has restricted access, Please refer to your system administrator'
							);
							break;

						// ==== request not found
						case 404:
							SwalManager.error(
								'Your request is not found, please try again later'
							);
							break;

						// ==== permissions updated
						case 409:
							return this.updatePermissions(error);

						default:
							break;
					}
				}

				return Promise.reject(error);
			}
		);
	}

	private updatePermissions(error: any) {
		BaseHandlerService.UpdatePermissions(error.response.data.permissions);

		const originalRequest = error.config;

		if (!originalRequest._retry) {
			originalRequest._retry = true;

			return ApiService.vueInstance.axios(originalRequest);
		}
	}

	private async invokeRefreshToken(error: any) {
		const originalRequest = error.config;

		if (!originalRequest._retry) {
			originalRequest._retry = true;

			const refreshToken = JwtService.getRefreshToken();

			if (refreshToken == null) {
				// occures when user open more than one tab then logout from one tab then go to any tab and call any api request
				// the refresh token removed by logout function
				router.push('/sign-in');
				location.reload();
			}

			return await ApiService.vueInstance.axios
				.get(`/Authentication/InvokeToken/${refreshToken}`)
				.then(async (response: BaseResponse<InvokeTokenResponse>) => {
					if (response.data.generateNewRefreshToken) {
						return await this.generateNewRefreshToken(error);
					}
					JwtService.saveToken(
						response.data.token as string,
						refreshToken as string
					);
					originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
					return await ApiService.vueInstance.axios(originalRequest);
				});
		}
	}

	private async generateNewRefreshToken(error: any) {
		return await ApiService.vueInstance.axios
			.get(
				`/Authentication/GenerateRefreshToken/${JwtService.getRefreshToken()}`
			)
			.then(async (response: BaseResponse<GenerateRefreshTokenResponse>) => {
				JwtService.saveToken(
					response.data.token as string,
					response.data.refreshToken as string
				);
				return await this.retryRequest(error);
			})
			.catch(async () => {
				this.destroyToken();
				router.push('/sign-in');
				location.reload();
			});
	}

	private async retryRequest(error: any): Promise<AxiosResponse> {
		const originalRequest = error.config;

		originalRequest._retry = true;
		originalRequest.headers.Authorization = `Bearer ${JwtService.getToken()}`;
		return await ApiService.vueInstance.axios(originalRequest);
	}

	private destroyToken() {
		const store = useAuthStore();
		store.purgeAuth();
	}

	/**
	 * @description set the default HTTP request headers
	 */
	public static setHeader(): void {
		ApiService.vueInstance.axios.defaults.headers.common[
			'Authorization'
		] = `Bearer ${JwtService.getToken()}`;
		ApiService.vueInstance.axios.defaults.headers.common['Accept'] =
			'application/json';
		ApiService.vueInstance.axios.defaults.headers.common['Accept-Language'] =
			'en';
	}

	/**
	 * @description send the GET HTTP request
	 * @param resource: string
	 * @param params: AxiosRequestConfig
	 * @returns Promise<AxiosResponse>
	 */
	public static query(resource: string, params: any): Promise<AxiosResponse> {
		return ApiService.vueInstance.axios.get(resource, params);
	}

	/**
	 * @description send the GET HTTP request
	 * @param resource: string
	 * @param slug: string
	 * @param showLoader: boolean
	 * @returns Promise<AxiosResponse>
	 */
	public static get(
		resource: string,
		slug = '' as string,
		showLoader: boolean = false
	): Promise<AxiosResponse> {
		return ApiService.vueInstance.axios.get(`${resource}/${slug}`, {
			headers: {
				SHOW_LOADER: showLoader,
			},
		});
	}

	/**
	 * @description set the POST HTTP request
	 * @param resource: string
	 * @param params: AxiosRequestConfig
	 * @param showLoader: boolean
	 * @returns Promise<AxiosResponse>
	 */
	public static post(
		resource: string,
		params: any,
		showLoader: boolean = false
	): Promise<AxiosResponse> {
		return ApiService.vueInstance.axios.post(`${resource}`, params, {
			headers: {
				SHOW_LOADER: showLoader,
			},
		});
	}

	/**
	 * @description send the UPDATE HTTP request
	 * @param resource: string
	 * @param slug: string
	 * @param params: AxiosRequestConfig
	 * @param showLoader: boolean
	 * @returns Promise<AxiosResponse>
	 */
	public static update(
		resource: string,
		slug: string,
		params: any,
		showLoader: boolean = false
	): Promise<AxiosResponse> {
		return ApiService.vueInstance.axios.put(`${resource}/${slug}`, params, {
			headers: {
				SHOW_LOADER: showLoader,
			},
		});
	}

	/**
	 * @description Send the PUT HTTP request
	 * @param resource: string
	 * @param params: AxiosRequestConfig
	 * @param showLoader: boolean
	 * @returns Promise<AxiosResponse>
	 */
	public static put(
		resource: string,
		params: any,
		showLoader: boolean = false
	): Promise<AxiosResponse> {
		return ApiService.vueInstance.axios.put(`${resource}`, params, {
			headers: {
				SHOW_LOADER: showLoader,
			},
		});
	}

	/**
	 * @description Send the DELETE HTTP request
	 * @param resource: string
	 * @param showLoader: boolean
	 * @returns Promise<AxiosResponse>
	 */
	public static delete(
		resource: string,
		showLoader: boolean = false
	): Promise<AxiosResponse> {
		return ApiService.vueInstance.axios.delete(resource, {
			headers: {
				SHOW_LOADER: showLoader,
			},
		});
	}
}

export default ApiService;
